import { useState, useCallback } from "react";

export const useForm = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback(
    (field) => (e) => {
      const { value } = e.target;
      setValues((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    },
    [errors]
  );

  const setValue = useCallback((field, value) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const setError = useCallback((field, error) => {
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  }, []);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setIsSubmitting(false);
  }, [initialValues]);

  const validate = useCallback(
    (validationRules) => {
      const newErrors = {};

      Object.keys(validationRules).forEach((field) => {
        const rule = validationRules[field];
        const value = values[field];

        if (rule.required && (!value || value.trim() === "")) {
          newErrors[field] = rule.message || `${field} is required`;
        } else if (rule.pattern && !rule.pattern.test(value)) {
          newErrors[field] = rule.message || `${field} is invalid`;
        } else if (rule.minLength && value.length < rule.minLength) {
          newErrors[field] =
            rule.message ||
            `${field} must be at least ${rule.minLength} characters`;
        } else if (rule.maxLength && value.length > rule.maxLength) {
          newErrors[field] =
            rule.message ||
            `${field} must be no more than ${rule.maxLength} characters`;
        }
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [values]
  );

  return {
    values,
    errors,
    isSubmitting,
    setIsSubmitting,
    handleChange,
    setValue,
    setError,
    reset,
    validate,
  };
};
